import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDesk } from '@/components/growth-desk/data/DeskData';
import { DESK } from '@/components/growth-desk/links';
import { cx, DeskButton, DeskLinkButton } from '@/components/growth-desk/ui';

/** Your business sections by onboarding step (BusinessPage STEP_OF). */
const TOPIC: Record<string, string> = { business: 'basics', customers: 'customers', offer: 'offer', goals: 'goals' };
const SNOOZE_DAYS = 7;
const WHAT: Record<'campaign' | 'post' | 'ad', string> = { campaign: 'This campaign', post: 'This post', ad: 'This ad' };

/**
 * At the start of making something (a campaign, a post, an ad) for a business whose details or
 * strategy aren't done: what is made is generic, and finishing them takes minutes. Never blocks;
 * "Not now" hides it for this business for a week. Gone for good once both are done.
 */
export const SetupFirstBanner: React.FC<{ where: 'campaign' | 'post' | 'ad'; className?: string }> = ({ where, className }) => {
  const { currentOrganization } = useAuth();
  const { onboarding, planState } = useDesk();
  const key = `admeru.setupFirst.snooze.${currentOrganization?.id || 'none'}`;
  const [snoozed, setSnoozed] = useState(() => {
    try { return Number(localStorage.getItem(key) || 0) > Date.now(); } catch { return false; }
  });
  if (!onboarding || !planState || snoozed) return null;
  const missing = onboarding.readiness.missing.filter(q => !q.optional);
  const approved = planState.status === 'ready';          // an approved strategy is in use
  if (!missing.length && approved) return null;
  const first = missing[0];
  const to = missing.length ? `${DESK.business}?topic=${TOPIC[first?.step || ''] || 'basics'}` : DESK.strategy;
  const n = missing.length;
  const todo = n
    ? `Answer ${n} quick question${n === 1 ? '' : 's'} (about ${Math.max(2, Math.ceil(n * 0.7))} minutes) and approve your strategy`
    : 'Approve your strategy (it’s written; reading it takes a few minutes)';
  const later = () => {
    try { localStorage.setItem(key, String(Date.now() + SNOOZE_DAYS * 864e5)); } catch { /* this visit only */ }
    setSnoozed(true);
  };
  return (
    <section aria-label="Finish setting up first" className={cx('flex flex-wrap items-center gap-x-5 gap-y-3 rounded-card bg-brand-soft px-5 py-4', className)}>
      <div className="flex min-w-0 flex-[1_1_380px] flex-col gap-1">
        <strong className="text-sm text-ink">{`${WHAT[where]} will be generic: Admeru doesn’t know your business well enough yet.`}</strong>
        <span className="text-small text-ink-2">{`${todo}, and every campaign, post and ad uses your offer, your customers and the channels that suit you.`}</span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <DeskLinkButton to={to} variant="primary" size="sm">{n ? 'Finish my business details' : 'Review my strategy'}</DeskLinkButton>
        <DeskButton size="sm" variant="plain" onClick={later}>Not now</DeskButton>
        <button type="button" onClick={later} aria-label="Hide for a week" className="grid h-8 w-8 place-items-center rounded-full text-ink-3 hover:text-ink">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
};
